// ============================================================
// Formations — progression d'un parcours (TypeScript pur)
// ============================================================
// Types structurels volontairement minimaux : le domaine ne dépend pas
// de `data-access`, il décrit juste la forme dont il a besoin.

export interface ProgressLesson {
  id: string;
  completedAt: string | null;
}

export interface ProgressChapter {
  id: string;
  lessons: ProgressLesson[];
}

export interface CourseProgress {
  totalLessons: number;
  completedLessons: number;
  /** 0-100, arrondi. Un parcours vide vaut 0. */
  percent: number;
  /** Vrai dès qu'il y a au moins une leçon et qu'elles sont toutes faites. */
  isComplete: boolean;
  /** Vrai tant que rien n'a été coché. */
  isUntouched: boolean;
  /** La première leçon non terminée dans l'ordre du parcours, ou null. */
  nextLessonId: string | null;
  /** Le chapitre qui la contient, ou null. */
  nextChapterId: string | null;
}

export function computeCourseProgress(chapters: ProgressChapter[]): CourseProgress {
  let total = 0;
  let completed = 0;
  let nextLessonId: string | null = null;
  let nextChapterId: string | null = null;

  for (const chapter of chapters) {
    for (const lesson of chapter.lessons) {
      total++;
      if (lesson.completedAt) {
        completed++;
      } else if (!nextLessonId) {
        nextLessonId = lesson.id;
        nextChapterId = chapter.id;
      }
    }
  }

  return {
    totalLessons: total,
    completedLessons: completed,
    percent: total === 0 ? 0 : Math.round((completed / total) * 100),
    isComplete: total > 0 && completed === total,
    isUntouched: completed === 0,
    nextLessonId,
    nextChapterId,
  };
}

/** Aplatit le parcours dans l'ordre de lecture — sert au « suivant ». */
export function flattenLessons<L extends ProgressLesson>(
  chapters: { id: string; lessons: L[] }[],
): L[] {
  return chapters.flatMap((c) => c.lessons);
}

/**
 * La leçon qui suit `currentId` dans l'ordre de lecture, ou null si
 * c'était la dernière.
 */
export function nextLessonAfter<L extends ProgressLesson>(
  chapters: { id: string; lessons: L[] }[],
  currentId: string,
): L | null {
  const flat = flattenLessons(chapters);
  const index = flat.findIndex((l) => l.id === currentId);
  if (index < 0) return null;
  return flat[index + 1] ?? null;
}
